import {Component, OnInit} from '@angular/core';
import {
    IOrderHistoryDateRange,
    IOrderHistoryView,
    IOrderHistoryViewProduct,
    IOrderHistoryViewProductDisplayAttribute
} from '../orders-models';
import {BaseComponent} from '../../base.component';
import {UserService} from '../../common/user.service';
import {LoggerService} from '../../common/logger.service';
import {Subscription} from 'rxjs';
import {OrderHistoryCacheService} from "./order-history-cache.service";
import {GqlOrderHistoryService} from "../../../checkout/graphql/order-history/gql-order-history.service";
import {MarkupService} from "../../common/markup.service";
import {BreadcrumbType} from "../../breadcrumb/breadcrumb.models";
import {BreadcrumbService} from "../../breadcrumb/breadcrumb.service";
import {TranslateService} from "../../translation/translate.service";
import {OrderHistoryService} from "./order-history.service";

@Component({
    selector: 'order-history',
    templateUrl: './order-history.component.html',
    styleUrls: ['../orders.scss', './order-history.component.scss'],
    standalone: false
})
export class OrderHistoryComponent extends BaseComponent implements OnInit {

    orderViews: IOrderHistoryView[];
    dateRanges: IOrderHistoryDateRange[];
    selectedDateRange: IOrderHistoryDateRange;
    subscription: Subscription = new Subscription();
    loading: boolean = false;

    headerLabels = [
        'cart.order.history.product.price',
        'cart.order.history.product.volume',
        'cart.order.history.product.quantity'
    ];

    productDisplayAttributes: IOrderHistoryViewProductDisplayAttribute[] = [
        {name: 'totalPrice', isCurrency: true},
        {name: 'totalVolume', isCurrency: false},
        {name: 'quantity', isCurrency: false}
    ];

    constructor(
        userService: UserService,
        loggerService: LoggerService,
        private markupService: MarkupService,
        private breadcrumbService: BreadcrumbService,
        private translateService: TranslateService,
        private orderHistoryService: OrderHistoryService,
        private orderHistoryCacheService: OrderHistoryCacheService,
        private gqlOrderHistoryService: GqlOrderHistoryService
    ) {
        super(userService, loggerService);
    }

    ngOnInit(): void {
        this.markupService.setTitleFromTranslationTag('cart.order.history');
        this.setBreadcrumb();
        this.init();
    }

    get previouslyLoaded(): boolean {
        return !!this.orderHistoryCacheService.getOrderViews() && !!this.orderHistoryCacheService.getDateRanges() && !!this.orderHistoryCacheService.getSelectedDateRange();
    }

    init() {
        if (this.previouslyLoaded) {
            this.dateRanges = this.orderHistoryCacheService.getDateRanges();
            this.selectedDateRange = this.orderHistoryCacheService.getSelectedDateRange();
            this.orderViews = this.orderHistoryCacheService.getOrderViews();
            this.scrollToActiveOrderView();
        } else {
            this.initDateRanges();
        }
    }

    initDateRanges() {
        this.loading = true;
        this.gqlOrderHistoryService.getDateRanges().subscribe({
            next: (dateRanges: IOrderHistoryDateRange[]) => this.setDateRanges(dateRanges),
            error: (error: any) => {
                this.log.error("Error fetching order history date ranges", error);
                this.loading = false;
            }
        });
    }

    setDateRanges(dateRanges: IOrderHistoryDateRange[]) {
        this.dateRanges = dateRanges;
        if (dateRanges?.length) {
            dateRanges.forEach((dateRange: IOrderHistoryDateRange) => {
                if (!dateRange.display) {
                    this.translateService.translate(`cart.order.history.range.${dateRange.tag}`).then((data: string) => dateRange.display = data, (error: any) => {
                        this.log.error("translate date range display", error);
                        dateRange.display = dateRange.tag;
                    });
                }
            });
            this.selectedDateRange = dateRanges[0];
            this.orderHistoryCacheService.setSelectedDateRange(this.selectedDateRange);
            this.initOrderViews();
        } else {
            this.loading = false;
        }
    }

    initOrderViews() {
        this.loading = true;
        this.gqlOrderHistoryService.getOrderViews(this.selectedDateRange).subscribe({
            next: (orderViews: IOrderHistoryView[]) => {
                this.orderViews = orderViews;
                this.loading = false;
                this.scrollToActiveOrderView();
            },
            error: (error: any) => {
                this.log.error("Error fetching order history", error);
                this.loading = false;
            }
        });
    }

    setBreadcrumb() {
        this.breadcrumbService.setBreadcrumb(null, BreadcrumbType.ORDER_HISTORY);
    }

    scrollToActiveOrderView() {
        const activeOrderView: IOrderHistoryView = this.orderHistoryCacheService.getActiveOrderView();
        if (activeOrderView) {
            // wait for the next event loop tick to ensure DOM is rendered
            setTimeout(() => {
                const element: HTMLElement = document.getElementById(`order${activeOrderView.id}`);
                if (element) {
                    element.scrollIntoView();
                }
            }, 0);
        }
    }

    onDateRangeChange() {
        this.orderHistoryCacheService.setSelectedDateRange(this.selectedDateRange);
        this.initOrderViews();
    }

    setActiveOrderView(orderView: IOrderHistoryView) {
        this.orderHistoryCacheService.setActiveOrderView(orderView);
    }

    addItemToCart(product: IOrderHistoryViewProduct, orderId: number) {
        this.orderHistoryCacheService.addProductAdding(product, orderId);
        this.orderHistoryService.addItemToCart(product, orderId);
    }

    addFullOrderToCart(products: IOrderHistoryViewProduct[], orderId: number) {
        this.orderHistoryCacheService.addAllProductsAdding(orderId);
        this.orderHistoryService.addFullOrderToCart(products, orderId);
    }

    viewTracking(orderView: IOrderHistoryView) {
        this.orderHistoryService.openTrackingModal(orderView);
    }

    getTrackingCount(orderView: IOrderHistoryView): number {
        return this.orderHistoryService.getTrackingCount(orderView);
    }
}
